/**
 * GeoLocations — Index pages for /locations, /locations/:state, /locations/:state/:city
 * These are crawlable directory pages that help Google discover all geo SEO pages.
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'Local Branding Pro';
const SITE_URL = 'https://localbrandingpro.com';

const SERVICE_LABELS = {
  'local-seo': 'Local SEO',
  'social-media': 'Social Media Marketing',
  'reputation-management': 'Reputation Management',
  'google-ads': 'Google Ads & PPC',
  'web-design': 'Web Design',
  'content-marketing': 'Content Marketing',
  'google-business-profile': 'Google Business Profile',
};

export function LocationsIndex() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/seo/geo-sitemap`)
      .then(r => r.json())
      .then(d => {
        // Group by state
        const states = {};
        (d.urls || []).forEach(u => {
          const st = u.state || 'Other';
          if (!states[st]) states[st] = { cities: new Set(), count: 0 };
          states[st].cities.add(u.city);
          states[st].count++;
        });
        setData({ total: d.total_pages, states });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Service Locations | {BRAND}</title>
        <meta name="description" content={`${BRAND} serves businesses across the United States. Browse our service locations.`} />
        <link rel="canonical" href={`${SITE_URL}/locations`} />
      </Helmet>
      <div style={{ minHeight: '100vh', background: '#fff' }}>
        <header style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#fff', padding: '48px 24px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <nav style={{ fontSize: 13, color: '#93c5fd', marginBottom: 12 }}>
              <Link to="/" style={{ color: '#93c5fd', textDecoration: 'none' }}>Home</Link> &rsaquo; <span>Locations</span>
            </nav>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 800, margin: 0 }}>Service Locations</h1>
            <p style={{ color: '#cbd5e1', marginTop: 8 }}>{BRAND} serves businesses across the United States. {data?.total || 0} pages covering {Object.keys(data?.states || {}).length} states.</p>
          </div>
        </header>
        <main style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
          {loading ? <p style={{ color: '#94a3b8' }}>Loading locations...</p> : (
            data?.states && Object.entries(data.states).sort(([a], [b]) => a.localeCompare(b)).map(([state, info]) => (
              <div key={state} style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{state}</h2>
                <p style={{ fontSize: 14, color: '#64748b' }}>{info.cities.size} cities, {info.count} pages</p>
              </div>
            ))
          )}
          {!loading && (!data?.states || Object.keys(data.states).length === 0) && (
            <p style={{ color: '#94a3b8' }}>No location pages generated yet. Check back soon!</p>
          )}
        </main>
      </div>
    </>
  );
}

export function StateLocations() {
  const { stateSlug } = useParams();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/seo/geo-sitemap`)
      .then(r => r.json())
      .then(d => {
        // Filter by state from URLs
        const statePages = (d.urls || []).filter(u => {
          const url = u.url || '';
          return url.includes(`/locations/${stateSlug}/`) || url.includes(`/${stateSlug}/`);
        });
        // Group by city
        const cities = {};
        statePages.forEach(p => {
          const c = p.city || 'Unknown';
          if (!cities[c]) cities[c] = [];
          cities[c].push(p);
        });
        setPages(cities);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [stateSlug]);

  const stateName = stateSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <>
      <Helmet>
        <title>{stateName} Service Locations | {BRAND}</title>
        <meta name="description" content={`${BRAND} digital marketing services in ${stateName}. Local SEO, social media, web design and more.`} />
        <link rel="canonical" href={`${SITE_URL}/locations/${stateSlug}`} />
      </Helmet>
      <div style={{ minHeight: '100vh', background: '#fff' }}>
        <header style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#fff', padding: '48px 24px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <nav style={{ fontSize: 13, color: '#93c5fd', marginBottom: 12 }}>
              <Link to="/" style={{ color: '#93c5fd', textDecoration: 'none' }}>Home</Link> &rsaquo;{' '}
              <Link to="/locations" style={{ color: '#93c5fd', textDecoration: 'none' }}>Locations</Link> &rsaquo;{' '}
              <span>{stateName}</span>
            </nav>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 800, margin: 0 }}>Digital Marketing in {stateName}</h1>
            <p style={{ color: '#cbd5e1', marginTop: 8 }}>Browse {BRAND} services available in {stateName} cities.</p>
          </div>
        </header>
        <main style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
          {loading ? <p style={{ color: '#94a3b8' }}>Loading...</p> : (
            Object.entries(pages).sort(([a], [b]) => a.localeCompare(b)).map(([city, services]) => (
              <div key={city} style={{ marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #e2e8f0' }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>{city}</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(SERVICE_LABELS).map(([sid, label]) => (
                    <Link key={sid} to={`/locations/${stateSlug}/${city.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}-${services[0]?.state?.toLowerCase() || stateSlug.split('-').pop()}/${sid}`}
                      style={{ fontSize: 13, color: '#1e3a8a', textDecoration: 'none', padding: '6px 12px', background: '#f1f5f9', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            ))
          )}
          {!loading && Object.keys(pages).length === 0 && (
            <p style={{ color: '#94a3b8' }}>No pages generated for {stateName} yet.</p>
          )}
        </main>
      </div>
    </>
  );
}

export function CityLocations() {
  const { stateSlug, citySlug } = useParams();
  const cityName = citySlug.replace(/-[a-z]{2}$/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const stateName = stateSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <>
      <Helmet>
        <title>{cityName} Digital Marketing Services | {BRAND}</title>
        <meta name="description" content={`${BRAND} offers Local SEO, social media, web design and more for ${cityName} businesses.`} />
        <link rel="canonical" href={`${SITE_URL}/locations/${stateSlug}/${citySlug}`} />
      </Helmet>
      <div style={{ minHeight: '100vh', background: '#fff' }}>
        <header style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#fff', padding: '48px 24px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <nav style={{ fontSize: 13, color: '#93c5fd', marginBottom: 12 }}>
              <Link to="/" style={{ color: '#93c5fd', textDecoration: 'none' }}>Home</Link> &rsaquo;{' '}
              <Link to="/locations" style={{ color: '#93c5fd', textDecoration: 'none' }}>Locations</Link> &rsaquo;{' '}
              <Link to={`/locations/${stateSlug}`} style={{ color: '#93c5fd', textDecoration: 'none' }}>{stateName}</Link> &rsaquo;{' '}
              <span>{cityName}</span>
            </nav>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 800, margin: 0 }}>Digital Marketing in {cityName}</h1>
            <p style={{ color: '#cbd5e1', marginTop: 8 }}>Choose a service to learn how {BRAND} can help your {cityName} business grow.</p>
          </div>
        </header>
        <main style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {Object.entries(SERVICE_LABELS).map(([sid, label]) => (
              <Link key={sid} to={`/locations/${stateSlug}/${citySlug}/${sid}`}
                style={{ display: 'block', padding: 24, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, textDecoration: 'none', color: '#0f172a', transition: 'border-color 0.2s' }}
                onMouseOver={e => e.currentTarget.style.borderColor = '#1e3a8a'}
                onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{label}</h2>
                <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
                  {label} services for {cityName} businesses — boost your local presence.
                </p>
              </Link>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}
